'use client'

import * as React from 'react'
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import { Circle } from 'lucide-react'

import { cn } from '../../utils/cn'

interface RadioGroupProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> {
  invalid?: boolean
  errorMessage?: string
  ['aria-label']?: string
  label?: string
  hint?: string
}

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupProps
>(({ className, invalid, errorMessage, label, hint, ...props }, ref) => {
  const uid = React.useId()

  React.useEffect(() => {
    if (!props['aria-label'] && !label) {
      throw new Error(
        'Radio Group elements require either a label or an aria-label for accessibility.'
      )
    }
  }, [])

  const renderDescription = () => {
    if (errorMessage) {
      return (
        <span
          id={`message-${uid}`}
          role="alert"
          className="text-error-700 text-xs"
        >
          {errorMessage}
        </span>
      )
    }
    if (hint) {
      return (
        <span id={`message-${uid}`} className="text-xs text-neutral-500">
          {hint}
        </span>
      )
    } else {
      return (
        <span role="presentation" style={{ visibility: 'hidden' }}>
          ...
        </span>
      )
    }
  }

  return (
    <div>
      {label && (
        <label
          htmlFor={`radio-${uid}`}
          className={cn(invalid && 'text-error-700', 'font-medium')}
        >
          {label}
        </label>
      )}
      <RadioGroupPrimitive.Root
        className={cn('grid gap-2', className)}
        {...props}
        id={`radio-${uid}`}
        aria-label={props['aria-label'] || label}
        aria-invalid={invalid}
        aria-describedby={`message-${uid}`}
        ref={ref}
      />

      {renderDescription()}
    </div>
  )
})
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, children, ...props }, ref) => {
  const id = React.useId()

  return (
    <div className="flex items-center gap-1">
      <RadioGroupPrimitive.Item
        id={`radio-item-${id}`}
        ref={ref}
        // eslint-disable-next-line tailwindcss/classnames-order
        className={cn(
          'h-6 w-6 rounded-full border-[1.5px] border-neutral-600 hover:ring-4 hover:ring-neutral-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3b82f680] disabled:cursor-not-allowed disabled:bg-neutral-300 disabled:opacity-50 aria-checked:border-periwinkle',
          className
        )}
        {...props}
      >
        <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
          <Circle className="fill-periwinkle text-periwinkle h-3.5 w-3.5" />
        </RadioGroupPrimitive.Indicator>
      </RadioGroupPrimitive.Item>

      <label htmlFor={`radio-item-${id}`} className="pl-3 cursor-pointer">
        {children}
      </label>
    </div>
  )
})
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName

export { RadioGroup, RadioGroupItem }
